import { Translation } from 'cng-common/features/translations'
import { IColumn } from 'core-components/library/list/types'
import { PartnerNameWithLink } from 'partner360/components/partnerNameWithLink'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { getTranslatedText, TTranslationFunction } from 'partner360/helpers/getTranslatedText'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { DashboardGroupBy } from '../../../model/types'
import { formatMoney } from '../../helpers/formatMoney'
import { BusinessPriorities } from '../components/businessPriorities/BusinessPriorities'
import { InitiativeColumn } from '../components/groupName/InitiativeColumn'
import { InitiativeStatusBadge } from '../components/initiativeStatusBadge/InitiativeStatusBadge'
import { InitiativePeriod } from '../components/periodDisplay/InitiativePeriod'
import { TGroupedInitiativeRow, TListGroupBy, TListSortField } from '../models'

import { getFinGoalTypeTranslationKey } from './getFinGoal'

import styles from './getColumns.module.scss'

export interface IListColumn extends Omit<IColumn<TGroupedInitiativeRow>, 'field'> {
  field: TListSortField
}
interface IGetColumns {
  isRollupMode: boolean
  groupBy: TListGroupBy
  actualsType: ActualsValueTypes
  activeCurrency: string
  t: TTranslationFunction
  onEdit?: (item: TGroupedInitiativeRow) => void
}

export const getListColumnFields = (
  groupBy: TListGroupBy,
  isRollupMode: boolean
): TListSortField[] => [
  'Name',
  ...(isRollupMode ? (['PartnerName', 'MarketL4', 'Country'] as const) : []),
  'developmentBreadcrumb',
  ...(groupBy !== DashboardGroupBy.BusinessPriority ? (['BusinessPriorities'] as const) : []),
  ...(groupBy !== DashboardGroupBy.BusinessUnit ? (['BusinessUnit'] as const) : []),
  ...(groupBy !== DashboardGroupBy.Period ? (['Period'] as const) : []),
  ...(groupBy !== DashboardGroupBy.Status ? (['Status'] as const) : []),
  'finGoal',
]
const COLUMN_WEIGHTS: Record<TListSortField, number> = {
  Name: 30,
  developmentBreadcrumb: 17,
  BusinessPriorities: 12,
  BusinessUnit: 12,
  Period: 10,
  Status: 10,
  finGoal: 11,
  PartnerName: 14,
  Country: 10,
  MarketL4: 10,
}

export const getColumns = ({
  isRollupMode,
  groupBy,
  actualsType,
  activeCurrency,
  t,
  onEdit,
}: IGetColumns): IListColumn[] => {
  const keys = TRANSLATION_KEYS.dashboard.table.columns
  const titleKeys: Record<TListSortField, string> = {
    Name: keys.name,
    developmentBreadcrumb: keys.strategicInitiative,
    BusinessPriorities: keys.businessPriorities,
    BusinessUnit: TRANSLATION_KEYS.listView.businessUnit,
    Period: keys.period,
    Status: keys.status,
    finGoal: keys.finGoal,
    PartnerName: keys.partnerName,
    MarketL4: keys.market,
    Country: keys.country,
  }
  const fields = getListColumnFields(groupBy, isRollupMode)
  const totalWeight = fields.reduce((sum, field) => sum + COLUMN_WEIGHTS[field], 0)
  return fields.map((field) => {
    const replaceParams =
      field === 'finGoal'
        ? [
            { key: '{actualsType}', value: actualsType },
            { key: '{activeCurrency}', value: activeCurrency },
          ]
        : undefined
    return {
      field,
      width: String((COLUMN_WEIGHTS[field] / totalWeight) * 100) + '%',
      className: field === 'finGoal' ? styles.numeric : undefined,
      title: getTranslatedText({ t, key: titleKeys[field], replaceParams }),
      titleRender: () => <Translation text={titleKeys[field]} replaceParams={replaceParams} />,
      renderFunction: (item: TGroupedInitiativeRow) => {
        switch (field) {
          case 'Name':
            return <InitiativeColumn item={item} onEdit={onEdit} />
          case 'developmentBreadcrumb': {
            const path =
              groupBy === DashboardGroupBy.StrategicInitiative
                ? item.strategicPath?.slice(1)
                : item.strategicPath
            return path?.length ? (
              <div className={styles.strategicPath}>
                {path.map((key, index) => (
                  <span
                    key={JSON.stringify([index, key])}
                    className={index > 0 ? styles.sub : undefined}
                  >
                    <Translation text={key} span />
                  </span>
                ))}
              </div>
            ) : (
              <span className={styles.empty}>-</span>
            )
          }
          case 'BusinessPriorities':
            return <BusinessPriorities names={item.businessPriorityNames} />
          case 'BusinessUnit':
            return item.BusinessUnit ? (
              <Translation text={item.BusinessUnit} span />
            ) : (
              <span className={styles.empty}>-</span>
            )
          case 'Period':
            return <InitiativePeriod period={item.Period} />
          case 'Status':
            return <InitiativeStatusBadge status={item.Status} />
          case 'finGoal':
            return (
              <div className={styles.finGoal}>
                <span>
                  {item.finGoal != null ? formatMoney(item.finGoal, activeCurrency) : '-'}
                </span>
                {item.finGoalType && (
                  <span className={styles.goalType}>
                    <Translation text={getFinGoalTypeTranslationKey(item.finGoalType)} span />
                  </span>
                )}
              </div>
            )
          case 'PartnerName':
            return item.PartnerId && item.PartnerName ? (
              <PartnerNameWithLink
                preserveCurrentUrlParams
                partnerId={item.PartnerId}
                partnerName={item.PartnerName}
              />
            ) : (
              <span className={styles.empty}>-</span>
            )
          default:
            return item[field] || '-'
        }
      },
    }
  })
}
